import React from 'react';

import { Spinner } from '@/components/ui/spinner';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { type Job, ResourceType } from '@/types';

interface ClientJobsTabProps {
  client: string;
}

const statusLabels: Record<string, string> = {
  JR: 'Running',
  JQ: 'Queued',
  JP: 'Pass',
  JF: 'Failed',
};

const statusColors: Record<string, string> = {
  JR: 'text-blue-500',
  JQ: 'text-chariot-text-tertiary',
  JP: 'text-green-500',
  JF: 'text-red-500',
};

function stripTimestamp(value: string): string {
  return value.replace(/#\d{4}-\d{2}-\d{2}T[\d:.]+Z$/, '');
}

export const ClientJobsTab: React.FC<ClientJobsTabProps> = ({ client }) => {
  const { data, status } = useGenericSearch(
    { label: ResourceType.Job, query: 'source:pius-discovery', desc: true },
    { email: client, refetchInterval: 5000 }
  );
  const jobs = (data?.jobs ?? []) as Job[];

  if (status === 'pending') {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="xl" />
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <p className="text-chariot-text-secondary py-8 text-center text-sm">
        No pius-discovery jobs found for this client.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="text-chariot-text-tertiary grid grid-cols-[1fr_120px_100px_160px] gap-2 px-4 py-2 text-xs font-medium uppercase">
        <span>Target</span>
        <span>Source</span>
        <span>Status</span>
        <span>Updated</span>
      </div>
      {jobs.map((job, idx) => {
        const state = stripTimestamp(job.status || '');
        return (
          <div
            key={job.key || idx}
            className="border-chariot-stroke bg-chariot-background-primary grid grid-cols-[1fr_120px_100px_160px] gap-2 rounded-lg border px-4 py-3 text-sm"
          >
            <span className="text-chariot-text-primary truncate">
              {job.dns || job.name || '\u2014'}
            </span>
            <span className="text-chariot-text-secondary truncate">
              {stripTimestamp(job.source || '') || '\u2014'}
            </span>
            <span
              className={statusColors[state] ?? 'text-chariot-text-secondary'}
            >
              {statusLabels[state] ?? state}
            </span>
            <span className="text-chariot-text-tertiary text-xs">
              {job.updated ? new Date(job.updated).toLocaleString() : '\u2014'}
            </span>
          </div>
        );
      })}
    </div>
  );
};
